import { INode } from 'shared/types/tree'
import { NodeType } from 'shared/types/ingestion'
import {
  breadthFirstNodeCheck,
  isReferenceNode,
  nodeForId,
  traverseTree,
} from '../../utility/TreeHelpers'
import {
  AllUpdatesForNodes,
  TypeCountType,
  UpdatesByType,
  XRefLinkNode,
  XRefRootNode,
} from './types'
import {
  createAnchor,
  createImgTag,
  wrapP,
} from '../../../ingestion/html_utility'
import {
  CreateNodeUpdateData,
  UpdateNodeUpdateData,
} from '../nodeUpdates/types'
import { createNote } from '../TreeHelpers'
import { idsForNode, UpdateMethod, UpdateType } from '../nodeUpdates/utility'
import { FilterType, NodeFilterType } from '../treeManipulation/types'
import { getFilteredResults } from '../treeManipulation/getFilteredResults'
import { updateTreeNode } from '../TreeRequests'
import { getAncestorInTreeForCondition } from '../utility/utility'

export const XRefRootKey = 'xRefRoot'
export const XRefIdKey = 'xRef'

export const nodeTypesNeedingLinks: string[] = [
  NodeType.LessonPlan,
  NodeType.Assessment,
  NodeType.Activity,
]

function isNodeTypeAKey(type: string): type is keyof TypeCountType {
  return nodeTypesNeedingLinks.includes(type)
}

export function typeCounts(tree: INode): TypeCountType {
  const counts = nodeTypesNeedingLinks.reduce<TypeCountType>(
    (acc, type) => {
      return {
        ...acc,
        [type]: 0,
      }
    },
    { total: 0 },
  )
  for (const { type } of traverseTree(tree)) {
    if (isNodeTypeAKey(type)) {
      counts[type] += 1
      counts.total += 1
    }
  }
  return counts
}

export function matchNodes(tree1: INode, tree2: INode) {
  return matchChildren(tree1, tree2).reduce<[string, string][]>(
    (acc, [n1, n2]) => {
      if (nodeTypesNeedingLinks.includes(n1.type as NodeType)) {
        acc.push([n1.id, n2.id])
      }
      acc.push(...matchNodes(n1, n2))
      return acc
    },
    [],
  )
}

function seemsMatch(n1?: INode, n2?: INode) {
  if (!n1 || !n2) return false
  const titlesMatch =
    n1.title === n2.title || n1.title?.split(':')[0] === n2?.title.split(':')[0]
  return n1.type === n2.type && titlesMatch
}

function matchChildren(tree1: INode, tree2: INode) {
  const childs1 = filterChildren(tree1)
  const childs2 = filterChildren(tree2)

  const matched: string[] = []

  // First do perfect matches
  const nodeMatch = childs1.reduce<[INode, INode][]>((acc, child) => {
    const matches = childs2.filter((c2) => {
      if (matched.includes(c2.id)) return false
      return seemsMatch(child, c2)
    })
    if (matches.length === 1) {
      matched.push(child.id, matches[0].id)
      return [...acc, [child, matches[0]]]
    }
    return acc
  }, [])

  // then try to match the rest
  return childs1
    .filter(({ id }) => !matched.includes(id))
    .reduce<[INode, INode][]>((acc, child) => {
      const matches = childs2.filter(
        ({ id, type }) => type === child.type && !matched.includes(id),
      )
      if (matches.length === 1) {
        matched.push(matches[0].id)
        return [...acc, [child, matches[0]]]
      }
      const match = matches.find((n) => seemsMatch(n, child))
      if (match) {
        matched.push(match.id)
        return [...acc, [child, match]]
      }
      return acc
    }, nodeMatch)
}

function filterChildren(node: INode) {
  const toFilter = [NodeType.Note, NodeType.SkillsList, NodeType.Question]
  return node.nodes.filter((child) => {
    if (isReferenceNode(child)) return false
    if (toFilter.includes(child.type as NodeType)) {
      return false
    }
    return true
  })
}

function createCrossRefLink({
  linkText,
  vanityId,
  destinationNodeId,
}: {
  linkText: string
  vanityId: string
  destinationNodeId: string
}) {
  const img = createImgTag({
    src: 'https://kiddom-media-production.s3.us-east-2.amazonaws.com/www-assets/link_icon.svg',
    attributes: {
      style: 'padding-left: 5px; padding-bottom: 5px;',
    },
  })
  const link = `/curriculum/${vanityId}/node/${destinationNodeId}`
  return wrapP(createAnchor({ link, newTab: true, display: linkText + img }))
}

function isXRefNode(node: INode): node is XRefLinkNode {
  return node.type === NodeType.Note && node.body?.[XRefIdKey]
}

export function isXRefRoot(node: INode): node is XRefRootNode {
  return !!node.body?.[XRefRootKey]?.language
}

export function getXRefRoots(tree: INode) {
  const subtrees: XRefRootNode[] = []
  breadthFirstNodeCheck(tree, (node) => {
    if (isXRefRoot(node)) {
      subtrees.push(node)
    }
  })
  return subtrees
}

export function getAllXRefNodes(tree: INode) {
  const nodeFilter: NodeFilterType = {
    filterType: FilterType.node,
    typeSelections: { filterType: 'including', types: [NodeType.Note] },
    body: { [XRefIdKey]: { type: 'equal', value: [/^.+$/] } },
  }
  return getFilteredResults<INode>(nodeFilter, tree)
}

function createLinkToNode({
  destinationNodeId,
  vanityId,
  fullTree,
}: {
  destinationNodeId: string
  vanityId: string
  fullTree: INode
}) {
  const destinationNode = nodeForId(destinationNodeId, fullTree)
  if (!destinationNode) return ''
  const ancestorData = getAncestorInTreeForCondition(
    destinationNode,
    isXRefRoot,
    fullTree,
  )
  if (!ancestorData || !isXRefRoot(ancestorData)) return ''
  return createCrossRefLink({
    linkText: `${ancestorData.body.xRefRoot.language} version`,
    destinationNodeId,
    vanityId,
  })
}

function createNoteNode({
  destinationNodeId,
  fullTree,
  vanityId,
}: {
  destinationNodeId: string
  vanityId: string
  fullTree: INode
}) {
  const linkString = createLinkToNode({
    destinationNodeId,
    fullTree,
    vanityId,
  })
  if (!linkString) return
  const noteNode = createNote({
    title: '',
    value: linkString,
    studentFacing: false,
  }) as XRefLinkNode
  noteNode.body[XRefIdKey] = destinationNodeId
  return noteNode
}

function xRefCreateData({
  mapping,
  fullTree,
  vanityId,
}: {
  mapping: [string, string]
  fullTree?: INode
  vanityId: string
}): CreateNodeUpdateData | undefined {
  if (!fullTree || !vanityId) return
  const [n1, n2] = mapping
  const noteNode = createNoteNode({
    destinationNodeId: n1,
    fullTree,
    vanityId,
  })
  if (!noteNode) return
  const parent = nodeForId(n2, fullTree)
  if (!parent) return
  const nextSibling = parent.nodes.find(
    ({ type }) => !type.startsWith('reference'),
  )
  return {
    type: UpdateType.node,
    ids: idsForNode(parent),
    params: {
      ...noteNode,
      next_sibling_node_id: nextSibling?.id,
    },
    method: UpdateMethod.create,
  }
}

function xRefUpdateData({
  toUpdate,
  destinationId,
  vanityId,
  fullTree,
}: {
  toUpdate: XRefLinkNode
  destinationId: string
  vanityId: string
  fullTree: INode
}): UpdateNodeUpdateData | undefined {
  const parent = nodeForId(toUpdate.parent_id, fullTree)
  if (!parent) return
  toUpdate.body.value = createLinkToNode({
    destinationNodeId: destinationId,
    fullTree,
    vanityId,
  })
  toUpdate.body.xRef = destinationId
  return {
    type: UpdateType.node,
    ids: idsForNode(parent),
    params: {
      ...toUpdate,
    },
    method: UpdateMethod.update,
  }
}

export function createCrossAssociationRoot({
  rootNode,
  language,
}: {
  rootNode: INode
  language: string
}) {
  const body = rootNode.body ?? {}
  body[XRefRootKey] = {
    language,
  }
  return updateTreeNode({
    tree_id: rootNode.tree_id,
    version: rootNode.version,
    id: rootNode.id,
    body,
  })
}

export function removeCrossAssociationRoot(node: INode) {
  if (!node.body) return
  const { xRef: _, ...body } = node.body
  return updateTreeNode({
    tree_id: node.tree_id,
    version: node.version,
    id: node.id,
    body,
  })
}

function getXRefRootForNode(nodeId: string, fullTree: INode) {
  const node = nodeForId(nodeId, fullTree)
  if (!node) return
  return getAncestorInTreeForCondition(
    node,
    isXRefRoot,
    fullTree,
  ) as XRefRootNode
}

export function getXRefLanguageForRootNode(node: INode) {
  return node.body?.[XRefRootKey].language
}

export function getXRefLanguageForNode(
  nodeId: string,
  fullTree: INode,
): string | undefined {
  const rootNode = getXRefRootForNode(nodeId, fullTree)
  if (!rootNode) return
  return getXRefLanguageForRootNode(rootNode)
}

export function getNodeRefUpdatesForNodes(
  parentIds: [string, string],
  vanityId: string,
  fullTree: INode,
) {
  return parentIds.reduce<AllUpdatesForNodes>((acc, parentId, index) => {
    const parent = nodeForId(parentId, fullTree)
    if (!parent) return acc
    const updates: UpdatesByType = {
      creates: [],
      updates: [],
      deletes: [],
    }
    const xRefNodes = parent.nodes.filter(isXRefNode) ?? []
    const otherNodeId = parentIds[index ^ 1]
    const otherRootLanguage = getXRefLanguageForNode(otherNodeId, fullTree)
    const goodNode = xRefNodes.find((node) => {
      const {
        body: { xRef, value },
      } = node
      return (
        otherRootLanguage &&
        xRef === otherNodeId &&
        value?.includes(otherNodeId) &&
        value.includes(otherRootLanguage)
      )
    })
    const badNodes = xRefNodes.filter(({ id }) => id !== goodNode?.id)
    if (!goodNode) {
      if (!badNodes.length) {
        const create = xRefCreateData({
          mapping: [parent.id, otherNodeId],
          fullTree,
          vanityId,
        })
        if (create) updates.creates.push(create)
      } else {
        const [toUpdate] = badNodes.splice(0, 1)
        const updateData = xRefUpdateData({
          toUpdate,
          destinationId: otherNodeId,
          fullTree,
          vanityId,
        })
        if (updateData) {
          updates.updates.push(updateData)
        }
      }
    }
    badNodes.forEach((badNode) => {
      updates.deletes.push({
        type: UpdateType.node,
        ids: idsForNode(badNode),
        method: UpdateMethod.delete,
      })
    })
    return {
      ...acc,
      [parent.id]: updates,
    }
  }, {})
}

export function doesTreeHaveXRefLinkIssues(tree: INode) {
  const [root1, root2, ...rest] = getXRefRoots(tree)
  // No XRef, No Problems
  if (!root1) return false
  // If we have XRef we must have exactly two roots
  if (rest.length || !root2) return true
  const matches = matchChildren(root1, root2)
  // We have issues if there is a status that is not Looks Good
  return !!matches.find((set) => {
    const ids = set.map(({ id }) => id) as [string, string]
    const updates = getNodeRefUpdatesForNodes(ids, 'VanId', tree)
    return Object.values(updates).find((u) =>
      Object.values(u).find((l) => l.length),
    )
  })
}

export function getLangCodeForNode(nodeId: string, fullTree: INode) {
  const rootNode = getXRefRootForNode(nodeId, fullTree)
  if (!rootNode) return
  const language = getXRefLanguageForRootNode(rootNode)
  if (language === 'Spanish') return 'es'
  if (language === 'English') return 'en'
  return
}
